import {
  AlcoveEdgeRole,
  alcoveRingEdges,
  alcoveRingVerts,
  alongLeg,
  edgeFrame,
  EdgeName,
  edgeSegments,
  FloorPlanRoom,
  makeLineFrame,
  Point,
  roomCeilingHeight,
  roomCorner,
  roomCornerLineFrame,
  samePoint,
  toWorld,
} from '@kaiizen/kai-floor-plan';
import {
  type GeoPath,
  group,
  type GroupPath,
  type LinePath,
  outline,
  type PerimRef,
} from '../math2d';

/**
 * Structural perimeter walk — the single source of truth for the room outline
 * and every wall-stroke / selection highlight / hit-test derived from it.
 *
 * The walk builds a recursive {@link GeoPath} tree (room → walls → alcoves →
 * sub-walls). One alcove's area perimeter excludes the mouth shared with the
 * room; a main wall joins its segments with flat gaps and ends at its corner;
 * the room chains the four walls into one closed clockwise loop. The same tree
 * is stroked normally for a wall or heavy for a selection, and hit-tested on
 * non-DOM platforms. Wall elements / future anti-walls and door/window glyphs
 * are applied as separate passes on top.
 */

/** Minimum drawable length — gaps/segments shorter than this are skipped. */
const EPS = 1e-7;

/** The selectable identity of an alcove ring sub-edge. */
function refForRole(edge: EdgeName, index: number, role: AlcoveEdgeRole): PerimRef {
  switch (role.kind) {
    case 'wall':
      return { k: 'alcoveWall', edge, index, wall: role.wall };
    case 'corner':
      return { k: 'alcoveCorner', edge, index, which: role.which };
  }
}

/**
 * An alcove as a group: its wall + chamfer line leaves, mouth excluded.
 *
 * When the alcove abuts a neighbour on the same edge, the touching side wall is
 * **dropped** (`drop.left` / `drop.right`): that shared wall is interior to the
 * merged footprint and must not be drawn — the caller bridges the two alcoves
 * with a single step-riser connector instead. This is what keeps the walk
 * agreeing with the boolean union (no double-drawn junction), so the one path
 * drives both the 2D and 3D renderers.
 */
function alcovePath(
  shape: FloorPlanRoom,
  edge: EdgeName,
  index: number,
  height: number,
  drop: { left?: boolean; right?: boolean } = {},
): GroupPath {
  const children: LinePath[] = alcoveRingEdges(shape, edge, edgeSegments(shape, edge)[index])
    .filter(e => {
      const r = e.role;
      if (drop.left && r.kind === 'wall' && r.wall === 'leftEdge') return false;
      if (drop.right && r.kind === 'wall' && r.wall === 'rightEdge') return false;
      return true;
    })
    .map(e => ({ kind: 'line', line: makeLineFrame(e.a, e.b), ref: refForRole(edge, index, e.role), height }));
  return group(children, { k: 'alcove', edge, index });
}

/** One edge segment's area perimeter (mouth excluded): alcove group, or a flat line. */
function segmentPath(shape: FloorPlanRoom, edge: EdgeName, index: number, height: number): GeoPath {
  const a = edgeSegments(shape, edge)[index];
  if (a.edgeSegmentType === 'alcove') return alcovePath(shape, edge, index, height);
  const fr = edgeFrame(edge, shape.width, shape.length);
  return {
    kind: 'line',
    line: makeLineFrame(toWorld(fr, a.position, 0), toWorld(fr, a.position + a.width, 0)),
    ref: { k: 'seg', edge, index },
    height,
  };
}

/**
 * One main wall as a group, walked corner→segments→corner. Each segment's path
 * is joined to its neighbours by flat gap lines; the trailing corner's chamfer
 * (if any) closes the wall to the next edge's start.
 *
 * `selected: true` omits the segments (and the trailing corner), yielding only
 * the flat runs of bare wall — the highlight shown when a wall is selected.
 */
export function wallPath(
  shape: FloorPlanRoom,
  edge: EdgeName,
  opts: { selected?: boolean } = {},
): GroupPath {
  const selected = opts.selected ?? false;
  const fr = edgeFrame(edge, shape.width, shape.length);
  const height = roomCeilingHeight(shape);
  const flatStart = alongLeg(roomCorner(shape, fr.startCorner), fr.horizontal);
  const flatEnd = fr.length - alongLeg(roomCorner(shape, fr.endCorner), fr.horizontal);
  const segs = edgeSegments(shape, edge)
    .map((seg, index) => ({ seg, index }))
    .sort((a, b) => a.seg.position - b.seg.position);
  const children: GeoPath[] = [];

  // A flat run of bare wall over the along-edge span [sA, sB].
  const flat = (sA: number, sB: number) => {
    if (sB - sA <= EPS) return;
    children.push({
      kind: 'line',
      line: makeLineFrame(toWorld(fr, sA, 0), toWorld(fr, sB, 0)),
      ref: { k: 'wall', edge, gs: sA, ge: sB },
      height,
    });
  };

  // Two consecutive alcoves abut when one's far end meets the next's start along
  // the edge (spans touch). Their shared wall is interior to the merged footprint,
  // so it's dropped and replaced by a step-riser connector — the walk then matches
  // the boolean union exactly (no double-drawn junction).
  const abuts = (i: number): boolean => {
    const a = segs[i]?.seg, b = segs[i + 1]?.seg;
    return !!a && !!b && a.edgeSegmentType === 'alcove' && b.edgeSegmentType === 'alcove'
      && Math.abs((a.position + a.width) - b.position) <= EPS;
  };

  let cursor = flatStart;
  for (let i = 0; i < segs.length; i++) {
    const { seg: a, index } = segs[i];
    const isAlcove = a.edgeSegmentType === 'alcove';
    // Alcoves occupy exactly [position, position+width] of the wall (no mouth flare).
    flat(cursor, a.position);
    if (!selected) {
      if (isAlcove) {
        const dropLeft = abuts(i - 1); // previous alcove abuts this one
        const dropRight = abuts(i); // this alcove abuts the next one
        children.push(alcovePath(shape, edge, index, height, { left: dropLeft, right: dropRight }));
        if (dropRight) {
          // Bridge this alcove's back-right corner (vert 4) to the next's back-left
          // corner (vert 1): the exposed step between two different-depth alcoves
          // (zero-length, hence skipped, when they share a depth → one flush wall).
          const next = segs[i + 1];
          const here = alcoveRingVerts(shape, edge, a)[4];
          const there = alcoveRingVerts(shape, edge, next.seg)[1];
          if (!samePoint(here, there)) {
            const deeper = (a.depth ?? 0) >= (next.seg.depth ?? 0);
            const ref = deeper
              ? { k: 'alcoveWall' as const, edge, index, wall: 'rightEdge' as const }
              : { k: 'alcoveWall' as const, edge, index: next.index, wall: 'leftEdge' as const };
            children.push({ kind: 'line', line: makeLineFrame(here, there), ref, height });
          }
        }
      } else {
        children.push(segmentPath(shape, edge, index, height));
      }
    }
    cursor = Math.max(cursor, a.position + a.width);
  }
  flat(cursor, flatEnd);

  if (!selected) {
    const lf = roomCornerLineFrame(shape, fr.endCorner);
    if (lf) children.push({ kind: 'line', line: lf, ref: { k: 'roomCorner', corner: fr.endCorner }, height });
  }
  return group(children, { k: 'wall', edge, gs: flatStart, ge: flatEnd });
}

const PERIMETER_EDGE_ORDER: readonly EdgeName[] = ['backEdge', 'rightEdge', 'frontEdge', 'leftEdge'];

/** The whole room outline as one closed clockwise BVH: room → walls → alcoves. */
export function roomPath(shape: FloorPlanRoom): GroupPath {
  return group(PERIMETER_EDGE_ORDER.map(edge => wallPath(shape, edge)), { k: 'room' });
}

/**
 * The perimeter of one edge segment's area as an ordered polyline, mouth
 * excluded — the path an alcove is both drawn and selection-highlighted with.
 */
export function edgeSegmentPath(shape: FloorPlanRoom, edge: EdgeName, index: number): Point[] {
  return outline(segmentPath(shape, edge, index, roomCeilingHeight(shape)));
}
